import { inject, Injectable } from '@angular/core';
import { DBService } from './db.service';
import { FileService } from './file.service';
import { WarehouseReportApiService } from './api/warehouse-report-api.service';
import { appDb } from 'dexie/app.db';
import { catchError, from, map, switchMap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { liveQuery } from 'dexie';
import { generateUUID } from 'helpers/string.helper';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReportService {
  protected dbService = inject(DBService);
  protected fileService = inject(FileService);
  protected warehouseReportApiService = inject(WarehouseReportApiService);
  constructor() {}

  getList(filter) {
    let dbQuery = appDb.warehouseReport.where('isDeleted').equals(0);
    if (filter.wareHouseId) {
      dbQuery = dbQuery.and((item) => item.wareHouseId === filter.wareHouseId);
    }
    if (filter.name) {
      dbQuery = dbQuery.and((item) =>
        item.name.toLowerCase().includes(filter.name.toLowerCase())
      );
    }
    if (filter.status) {
      dbQuery = dbQuery.and((item) => item.status == filter.status);
    }
    if (filter.companyIds?.length) {
      dbQuery = dbQuery.and((item) =>
        filter.companyIds.includes(item.companyId)
      );
    }
    if (filter.warehouseIds?.length) {
      dbQuery = dbQuery.and((item) =>
        filter.warehouseIds.includes(item.wareHouseId)
      );
    }
    if (filter.userIds?.length) {
      dbQuery = dbQuery.and((item) => filter.userIds.includes(item.userId));
    }
    let subDB;
    if (
      filter.addressGeneralErrorIds?.length ||
      filter.warehouseGeneralErrorIds?.length ||
      filter.orderGeneralErrorIds?.length
    ) {
      const mergedItems = [
        ...(filter.addressGeneralErrorIds || []),
        ...(filter.warehouseGeneralErrorIds || []),
        ...(filter.orderGeneralErrorIds || []),
      ];
      subDB = appDb.warehouseReportError
        .where(['isDeleted', 'typeErrorId'])
        .anyOf(mergedItems.map((item) => [0, item]));
    }
    return from(
      liveQuery(() =>
        subDB
          ? subDB
              .toArray()
              .then((res) =>
                Array.from(new Set(res.map((item) => item.wareHouseReportId)))
              )
              .then((res: Array<string>) =>
                dbQuery
                  .and((item) => !res || res.includes(item.id))
                  .reverse()
                  .sortBy('createdAt')
              )
          : dbQuery.reverse().sortBy('createdAt')
      )
    );
  }

  getReport(id) {
    return from(liveQuery(() => appDb.warehouseReport.get(id)));
  }

  getWarehouseReports(warehouseId: string) {
    let dbQuery = appDb.warehouseReport
      .where(['isDeleted', 'wareHouseId'])
      .equals([0, warehouseId]);
    return from(liveQuery(() => dbQuery.toArray()));
  }

  getReportsCount() {
    return from(liveQuery(() => appDb.warehouseReport.count()));
  }

  getLatestReportForWarehouse(warehouseId) {
    return from(
      liveQuery(() =>
        appDb.warehouseReport
          .where(['isDeleted', 'wareHouseId', 'status'])
          .equals([0, warehouseId, 0])
          .sortBy('reportDate')
          .then((res) => res.at(-1))
      )
    );
  }

  create(data) {
    return this.warehouseReportApiService.create(data).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.createRow(appDb.warehouseReport, {
            id: generateUUID(),
            ...data,
            isSynced: 0,
            isDeleted: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  update(id, data) {
    delete data.isDeleted;
    delete data.isSynced;
    return this.warehouseReportApiService.update(id, data).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.warehouseReport, {
            ...data,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  move(data: {
    wareHouseReportId: string;
    fromWareHouseId: string;
    toWareHouseId: string;
  }) {
    return this.warehouseReportApiService
      .move(data.wareHouseReportId, data.fromWareHouseId, data.toWareHouseId)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (!navigator.onLine) {
            return this.dbService.updateRow(appDb.warehouseReport, {
              id: data.wareHouseReportId,
              wareHouseId: data.toWareHouseId,
              ...data,
              isSynced: 0,
            });
          }
          return throwError(() => err);
        })
      );
  }

  uploadSignedDoc(id, file, extraFileName) {
    return this.warehouseReportApiService.sign(id, file, extraFileName).pipe(
      catchError((err) => {
        if (!navigator.onLine) {
          return this.fileService
            .uploadFileLocal(file, {
              type: 'reportTemplate',
              id,
              replaceWith: 'filePath',
            })
            .pipe(map((hash) => `local://${hash}`));
        }
        return throwError(() => err);
      })
    );
  }

  delete(id) {
    return this.warehouseReportApiService.delete(id).pipe(
      switchMap((res: any) => this.dbService.deleteRow(appDb.company, id)),
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.company, {
            id,
            isDeleted: 1,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  getNotSyncedReports() {
    return from(appDb.warehouseReport.where('isSynced').equals(0).toArray());
  }

  getNotSyncedItemsCount() {
    return from(
      liveQuery(() => appDb.warehouseReport.where('isSynced').equals(0).count())
    );
  }
  bulkUpdateLocal(items) {
    return from(appDb.warehouseReport.bulkPut(items));
  }
}
